import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { motion, AnimatePresence } from 'framer-motion';
import userApi from '../../api/userApi';
import { getWCProvider, getWalletSessions, getStoredSession, purgeWCSession, isStaleSessionError, connectWithTimeout, WC_CONNECT_TIMEOUT, isTelegramWebview, WC_WALLETS, setLastWallet, getLastWallet, pingWCSession } from '../../utils/walletConnect';
import walletIcon from '../../assets/icon/wallet.png';
import oxyWalletIcon from '../../assets/icon/wallet_connect_oxy.png';
import rainbowIcon from '../../assets/icon/wallet_rainbow.png';
import rabbyIcon from '../../assets/icon/wallet_rabby.png';

const WALLET_ICONS = { rainbow: rainbowIcon, rabby: rabbyIcon };

const short = (addr) => addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '';

// ─── Icons ────────────────────────────────────────────────────────────────────
const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);
const CopyIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2"/>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
);
const PlugIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 2v6"/><path d="M15 2v6"/>
        <rect x="6" y="8" width="12" height="6" rx="2"/>
        <path d="M12 14v4a4 4 0 0 1-4 4"/>
    </svg>
);
const TrashIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
);

// ─── Bottom Sheet ─────────────────────────────────────────────────────────────
function Sheet({ isOpen, onClose, children }) {
    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={onClose}
                    />
                    <motion.div
                        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-[20px] bg-[#121212] border-t border-[#353232] pb-8"
                        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                    >
                        <div className="flex justify-center pt-3 pb-1">
                            <div className="h-1 w-10 rounded-full bg-white/20" />
                        </div>
                        <button
                            onClick={onClose}
                            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/60 text-[18px] leading-none"
                        >×</button>
                        {children}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
// Props:
//   open       – controlled: boolean (parent opens/closes the sheet)
//   onClose    – controlled: called when user closes the sheet
//   onConnected(addr|null) – fires when wallet connects / disconnects
export default function ConnectWallet({ open, onClose, onConnected }) {
    const [address, setAddress]       = useState('');
    const [chainId, setChainId]       = useState('');
    const [loading, setLoading]       = useState(false);
    const [copiedId, setCopiedId]     = useState(null);
    const [error, setError]           = useState('');
    // Все привязанные кошельки юзера (user_wallets на бэке)
    const [wallets, setWallets]       = useState([]);
    // Живые WC-сессии: { rainbow: '0x…', rabby: '0x…' } — могут жить одновременно
    const [sessions, setSessions]     = useState({});
    // Активный кошелёк — он подписывает транзакции
    const [activeKey, setActiveKey]   = useState(getLastWallet());
    // true = юзер нажал «Add wallet» — показываем экран подключения поверх списка
    const [showConnect, setShowConnect] = useState(false);
    // id кошелька, над которым идёт операция (primary/удаление)
    const [busyId, setBusyId]         = useState(null);
    // Здоровье сессий: { key: bool } — false = кошелёк сессию забыл
    const [health, setHealth]         = useState({});

    const hasInjected = typeof window !== 'undefined' && Boolean(window.ethereum);

    const loadWallets = useCallback(async () => {
        try {
            const list = await userApi.getWallets();
            if (Array.isArray(list)) { setWallets(list); return list; }
        } catch {}
        return [];
    }, []);

    const loadSessions = useCallback(async () => {
        try {
            const map = await getWalletSessions();
            setSessions(map);
            // Живость: спящие кошельки — по expiry из storage,
            // активный — настоящим пингом через relay.
            Object.keys(map).forEach((key) => {
                const stored = getStoredSession(key);
                setHealth((h) => ({ ...h, [key]: !stored?.expired }));
            });
            pingWCSession(getLastWallet()).then((alive) => {
                if (alive === false) {
                    setHealth((h) => ({ ...h, [getLastWallet()]: false }));
                }
            }).catch(() => {});
            return map;
        } catch { return {}; }
    }, []);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const user = await userApi.getMe();
                let addr = user?.walletAddress || '';
                let chain = user?.walletChainId || '';

                // Список всех привязанных кошельков; primary — фолбэк для легаси-поля
                const list = await loadWallets();
                if (!addr && list.length > 0) {
                    const primary = list.find(w => w.isPrimary) || list[0];
                    addr = primary?.walletAddress || '';
                    chain = primary?.walletChainId || '';
                }

                if (mounted && addr) {
                    setAddress(addr);
                    setChainId(chain);
                    onConnected?.(addr);
                }

                // Telegram перезагружает вебвью при возврате из кошелька, и код,
                // ждавший connect(), умирает — сессии остаются в localStorage,
                // а адрес может быть не сохранён. Подхватываем их и досохраняем.
                try {
                    const map = await loadSessions();
                    if (!mounted) return;
                    await ensureSessionsSaved(map, list);
                    const actKey = map[getLastWallet()] ? getLastWallet() : Object.keys(map)[0];
                    const wcAddr = actKey ? map[actKey] : null;
                    if (wcAddr) {
                        if (actKey !== getLastWallet()) setLastWallet(actKey);
                        setActiveKey(actKey);
                        if (!mounted) return;
                        setAddress(wcAddr);
                        onConnected?.(wcAddr);
                    }
                } catch {}
            } catch {}
        })();

        // Смена счёта в приложении кошелька (accountsChanged) — перечитываем
        // сессии и привязываем новый адрес
        const onSessionsChanged = async () => {
            try {
                const map = await loadSessions();
                await ensureSessionsSaved(map, await loadWallets());
            } catch {}
        };
        window.addEventListener('wc:sessions-changed', onSessionsChanged);
        return () => {
            mounted = false;
            window.removeEventListener('wc:sessions-changed', onSessionsChanged);
        };
    }, []);

    // Каждый адрес живой сессии должен быть привязан на бэке — иначе бэк
    // откажет в подписи депозита («не твой адрес»)
    const ensureSessionsSaved = async (map, list) => {
        let added = false;
        for (const [key, addr] of Object.entries(map)) {
            const known = list.some(w => w.walletAddress?.toLowerCase() === addr.toLowerCase());
            if (known) continue;
            try {
                // Ядро спящего кошелька не поднимаем — имя берём из справочника,
                // сеть у нас одна (Arbitrum)
                const name = WC_WALLETS.find((x) => x.key === key)?.name || 'Wallet';
                await userApi.saveWalletAddress(addr, '0xa4b1', `${name} (WalletConnect)`);
                added = true;
            } catch {}
        }
        if (added) await loadWallets();
    };

    const close = () => { setError(''); setShowConnect(false); onClose?.(); };

    const saveWallet = useCallback(async (addr, chain, label) => {
        setLoading(true); setError('');
        try {
            try {
                await userApi.saveWalletAddress(addr, chain, label);
            } catch (err) {
                // Повторный коннект уже привязанного кошелька — не ошибка,
                // просто освежили сессию
                const msg = String(err?.response?.data?.message || '');
                if (!(err?.response?.status === 400 && msg.includes('already connected'))) throw err;
            }
            setAddress(addr);
            setChainId(chain || '');
            setActiveKey(getLastWallet());
            await loadWallets();
            await loadSessions();
            setShowConnect(false);
            onConnected?.(addr);
            onClose?.();
        } catch (err) {
            // Показываем причину: без неё «Could not save wallet» не отладить с телефона
            const status = err?.response?.status;
            const msg = err?.response?.data?.message;
            setError(status
                ? `Could not save wallet (${status}${msg ? `: ${msg}` : ''})`
                : 'Could not save wallet (network/CORS)');
        } finally {
            setLoading(false);
        }
    }, [onConnected, onClose, loadWallets]);

    const connectInjected = async () => {
        if (!hasInjected) return;
        setLoading(true); setError('');
        try {
            const provider = new ethers.BrowserProvider(window.ethereum);
            const accounts = await provider.send('eth_requestAccounts', []);
            const network  = await provider.getNetwork();
            await saveWallet(accounts[0], '0x' + network.chainId.toString(16), 'Browser extension');
        } catch (e) {
            setError(e.code === 4001 ? 'Rejected' : 'Browser wallet error');
            setLoading(false);
        }
    };

    // Извлекаем имя кошелька из метаданных WalletConnect-сессии
    const getWCWalletLabel = (provider) => {
        const meta = provider?.session?.peer?.metadata;
        return meta?.name ? `${meta.name} (WalletConnect)` : 'WalletConnect';
    };

    // Сразу после connect() accounts бывает ещё пустым (сессия есть, список
    // аккаунтов не доехал) — дожимаем явным eth_accounts, иначе на бэк
    // улетал walletAddress: undefined и падал 500.
    const getWCAccount = async (provider) => {
        let addr = provider.accounts?.[0];
        if (!addr) {
            try {
                const accounts = await provider.request({ method: 'eth_accounts' });
                addr = accounts?.[0];
            } catch {}
        }
        if (!addr) throw new Error('NO_ACCOUNT');
        return addr;
    };

    // Подключение кошелька по ключу. Сессии кошельков живут ПАРАЛЛЕЛЬНО:
    // живую сессию не пересоздаём, а просто делаем кошелёк активным.
    const connectWalletConnect = async (walletKey) => {
        const key = walletKey || getLastWallet();
        setLastWallet(key);
        setActiveKey(key);
        setLoading(true); setError('');

        const tryConnect = async () => {
            const wcProvider = await getWCProvider(key);
            if (!wcProvider.session) {
                await connectWithTimeout(wcProvider);
            }
            const addr     = await getWCAccount(wcProvider);
            const chainHex = '0x' + wcProvider.chainId.toString(16);
            await saveWallet(addr, chainHex, getWCWalletLabel(wcProvider));
        };

        try {
            await tryConnect();
        } catch (e) {
            // Stale-session попадает сюда если упал init или connect — чистим и пробуем ещё один раз
            if (isStaleSessionError(e)) {
                try {
                    await purgeWCSession(key);
                    await tryConnect();
                    return;
                } catch (retryErr) {
                    e = retryErr;
                }
            }
            if (String(e?.message) === 'NO_ACCOUNT') {
                setError('Wallet connected but returned no address. Try again.');
                setLoading(false);
                return;
            }
            // Любая неудача оставляет за собой недоеденное спаривание — чистим,
            // чтобы следующее нажатие стартовало с нуля, а не спотыкалось об огрызки
            try { await purgeWCSession(key); } catch {}
            if (String(e?.message) === WC_CONNECT_TIMEOUT) {
                setError('Wallet did not respond. Try again — and make sure your wallet app is up to date.');
            } else if (e?.message?.includes('User rejected')) {
                setError('Rejected by user');
            } else {
                setError('WalletConnect error');
            }
            setLoading(false);
        }
    };

    // Мгновенное переключение активного кошелька на живую сессию
    const activateSession = async (key, addr) => {
        setLastWallet(key);
        setActiveKey(key);
        onConnected?.(addr);
        // Поднимаем ядро выбранного кошелька (прежнее усыпляется) и сразу
        // проверяем живость сессии настоящим пингом
        try {
            await getWCProvider(key);
            const alive = await pingWCSession(key);
            setHealth((h) => ({ ...h, [key]: alive !== false }));
        } catch {}
    };

    const removeOne = async (w) => {
        if (busyId) return;
        setBusyId(w.id);
        try {
            await userApi.removeWallet(w.id);
            // Гасим ВСЕ сессии с этим адресом (один адрес может жить и в
            // Rainbow, и в Rabby) — иначе остаётся полумёртвая сессия-зомби,
            // о которую спотыкается следующий коннект
            const matched = Object.entries(sessions).filter(
                ([, a]) => a?.toLowerCase() === w.walletAddress?.toLowerCase()
            );
            for (const [k] of matched) {
                try { await purgeWCSession(k); } catch {}
            }
            if (matched.length) await loadSessions();
            const list = await loadWallets();
            const primary = list.find(x => x.isPrimary) || list[0];
            setAddress(primary?.walletAddress || '');
            setChainId(primary?.walletChainId || '');
            onConnected?.(primary?.walletAddress || null);
        } catch {}
        setBusyId(null);
    };

    const copyAddress = (w) => {
        try { navigator.clipboard.writeText(w.walletAddress); } catch {}
        setCopiedId(w.id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    return (
        <>
            {/* ── Wallets list sheet ── */}
            <Sheet isOpen={open && wallets.length > 0 && !showConnect} onClose={close}>
                <div className="px-5 pt-2 pb-2">
                    <h2 className="text-center text-[19px] font-bold text-[#EAEAEA] mb-1">Wallets</h2>
                    <p className="text-center text-[12px] text-[#959595] mb-4">
                        Transactions are signed by the wallet you connect
                    </p>

                    {error && (
                        <div className="mb-3 rounded-[10px] border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-300 text-center">
                            {error}
                        </div>
                    )}

                    {/* Подключённые приложения-кошельки. Тап по строке = этот
                        кошелёк платит и подписывает. Отдельно от списка адресов:
                        один адрес может жить и в Rainbow, и в Rabby. */}
                    {/* Подключённые кошельки. Тап = этот кошелёк платит и подписывает. */}
                    {WC_WALLETS.filter((w) => sessions[w.key]).map((w) => {
                        const isAct = w.key === activeKey;
                        const row = wallets.find(
                            (x) => x.walletAddress?.toLowerCase() === sessions[w.key]?.toLowerCase()
                        );
                        return (
                            <div
                                key={`s-${w.key}`}
                                className={`w-full flex items-center gap-3 rounded-[10px] border px-4 py-3 mb-2 bg-[#252323] transition ${
                                    isAct ? 'border-emerald-400/40' : 'border-[#353232]'
                                }`}
                            >
                                <button
                                    onClick={async () => {
                                        if (health[w.key] === false) {
                                            // Кошелёк забыл сессию — чистим и переподключаем
                                            try { await purgeWCSession(w.key); } catch {}
                                            await loadSessions();
                                            connectWalletConnect(w.key);
                                            return;
                                        }
                                        activateSession(w.key, sessions[w.key]);
                                    }}
                                    className="flex-1 min-w-0 flex items-center gap-3 text-left"
                                >
                                    <img src={WALLET_ICONS[w.key] || walletIcon} alt={w.name} className="w-7 h-7 object-contain flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[14px] font-semibold text-[#EAEAEA]">{w.name}</p>
                                        <p className="font-mono text-[11px] text-[#959595] truncate">{short(sessions[w.key])}</p>
                                    </div>
                                    {health[w.key] === false ? (
                                        <span className="text-[11px] text-amber-300 flex-shrink-0">expired · tap to reconnect</span>
                                    ) : isAct ? (
                                        <span className="flex items-center gap-1.5 text-emerald-400 text-[11px] font-semibold flex-shrink-0">
                                            <CheckIcon /> pays
                                        </span>
                                    ) : (
                                        <span className="text-[11px] text-[#959595] flex-shrink-0">tap to use</span>
                                    )}
                                </button>
                                {row && (
                                    <button
                                        onClick={() => removeOne(row)}
                                        disabled={busyId === row.id}
                                        className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-white/15 text-white/35 flex-shrink-0 transition active:text-rose-400 active:border-rose-400/30"
                                    >
                                        <TrashIcon />
                                    </button>
                                )}
                            </div>
                        );
                    })}

                    {/* Адреса без живой сессии (подключались с другого устройства
                        и т.п.) — показываем только если такие есть */}
                    {(() => {
                        const liveAddrs = Object.values(sessions).map((a) => a?.toLowerCase());
                        const offline = wallets.filter(
                            (w) => !liveAddrs.includes(w.walletAddress?.toLowerCase())
                        );
                        if (!offline.length) return null;
                        return (
                            <>
                                <p className="text-[11px] text-[#959595] mt-3 mb-2 px-1">Other addresses</p>
                                {offline.map((w) => {
                                    const busy = busyId === w.id;
                                    return (
                                        <div
                                            key={w.id}
                                            className={`w-full flex items-center gap-3 rounded-[10px] border border-[#353232] px-4 py-3 mb-2 bg-[#252323] ${busy ? 'opacity-50' : ''}`}
                                        >
                                            <button onClick={() => copyAddress(w)} className="flex-1 min-w-0 text-left">
                                                <p className="font-mono text-[13px] text-white/85 truncate flex items-center gap-1.5">
                                                    {short(w.walletAddress)}
                                                    <span className="text-white/30">
                                                        {copiedId === w.id
                                                            ? <span className="text-emerald-400"><CheckIcon /></span>
                                                            : <CopyIcon />}
                                                    </span>
                                                </p>
                                                <p className="text-[10px] text-[#959595] truncate">{w.label || 'Wallet'}</p>
                                            </button>
                                            <button
                                                onClick={() => removeOne(w)}
                                                disabled={busy}
                                                className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-white/15 text-white/35 flex-shrink-0 transition active:text-rose-400 active:border-rose-400/30"
                                            >
                                                <TrashIcon />
                                            </button>
                                        </div>
                                    );
                                })}
                            </>
                        );
                    })()}

                    {/* add wallet */}
                    <button
                        onClick={() => { setError(''); setShowConnect(true); }}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 rounded-[10px] bg-[#121212] border border-white/20 px-4 py-3.5 mt-1 text-[13px] font-semibold text-[#EAEAEA] transition active:bg-white/5 disabled:opacity-50"
                    >
                        + Add wallet
                    </button>
                </div>
            </Sheet>

            {/* ── Connect wallet sheet (нет кошельков или нажали Add wallet) ── */}
            <Sheet isOpen={open && (wallets.length === 0 || showConnect)} onClose={close}>
                <div className="px-5 pt-2 pb-2">
                    {/* Hero */}
                    <div className="flex flex-col items-center pt-1 pb-5">
                        <img
                            src={oxyWalletIcon}
                            alt="wallet"
                            className="w-[72px] h-[72px] rounded-full mb-4"
                        />
                        <h2 className="text-[19px] font-bold text-[#EAEAEA] mb-1.5">Connect your wallet</h2>
                        <p className="text-center text-[13px] text-[#959595] max-w-[280px] leading-snug">
                            {isTelegramWebview()
                                ? 'Confirm the connection in your wallet app, then return here'
                                : 'Choose your wallet in the next step'}
                        </p>
                    </div>

                    {error && (
                        <div className="mb-4 rounded-[10px] border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-300 text-center">
                            {error}
                        </div>
                    )}

                    {isTelegramWebview() ? (
                        /* В Telegram модалка WC теряет uri в диплинке — открываем
                           кошелёк сами, поэтому выбор кошелька живёт здесь */
                        WC_WALLETS.map((w) => (
                            <button
                                key={w.key}
                                onClick={() => connectWalletConnect(w.key)}
                                disabled={loading}
                                className="w-full flex items-center gap-3 rounded-[10px] px-4 py-[14px] mb-3 bg-[#121212] border border-white/20 transition active:bg-white/5 active:scale-[0.98] disabled:opacity-50"
                            >
                                <div className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-white/20 flex-shrink-0 overflow-hidden">
                                    {loading ? (
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <img
                                            src={WALLET_ICONS[w.key] || walletIcon}
                                            alt={w.name}
                                            className="w-6 h-6 object-contain"
                                        />
                                    )}
                                </div>
                                <span className="text-[15px] font-semibold text-[#EAEAEA] flex-1 text-left">
                                    {loading ? 'Opening wallet…' : w.name}
                                </span>
                                {!loading && <span className="text-[16px] text-[#959595]">›</span>}
                            </button>
                        ))
                    ) : (
                        /* Обычный браузер — модалка WC с QR как раньше */
                        <button
                            onClick={connectWalletConnect}
                            disabled={loading}
                            className="w-full flex items-center gap-3 rounded-2xl bg-[#3478F6] px-5 py-4 mb-3 transition active:opacity-80 disabled:opacity-50"
                        >
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 flex-shrink-0">
                                {loading ? (
                                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <img src={walletIcon} alt="" width={20} height={20} />
                                )}
                            </div>
                            <div className="flex flex-col items-start flex-1 min-w-0">
                                <span className="text-[15px] font-semibold text-white">
                                    {loading ? 'Opening wallet…' : 'Connect Wallet'}
                                </span>
                                <span className="text-[11px] text-white/55">via WalletConnect</span>
                            </div>
                        </button>
                    )}

                    {/* Secondary: browser extension (only when injected provider exists) */}
                    {hasInjected && (
                        <button
                            onClick={connectInjected}
                            disabled={loading}
                            className="w-full flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3.5 mb-3 transition active:bg-white/10 disabled:opacity-50"
                        >
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/70 flex-shrink-0">
                                <PlugIcon />
                            </div>
                            <div className="flex flex-col items-start flex-1 min-w-0">
                                <span className="text-[14px] font-medium text-white">Use browser extension</span>
                                <span className="text-[11px] text-white/40">MetaMask, Rabby, etc.</span>
                            </div>
                        </button>
                    )}

                </div>
            </Sheet>
        </>
    );
}
